import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, router } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../components/layout/TeacherShell';
import { Card, Button, Textarea } from '../../components/ui';
import QuestionCreationForm from '../../components/questions/QuestionCreationForm';
import ImportModal from '../../components/questions/ImportModal';
import SourceBadge from '../../components/questions/SourceBadge';
import StimulusSuggestionBanner from '../../components/questions/StimulusSuggestionBanner';
import QuestionTypeSelector from '../../components/questions/QuestionTypeSelector';
import OptionsBuilder from '../../components/questions/OptionsBuilder';
import { cn } from '../../lib/cn';

const TYPE_LABELS = {
  short_answer: 'Short Answer',
  multiple_choice: 'MCQ',
  mcq: 'MCQ',
  essay: 'Essay',
  fill_blank: 'Fill Blank',
  table_completion: 'Table',
  vst_mcq: 'VST',
};

export default function QuestionBank({ questions, lesson, course_lessons, stimuli = [] }) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [includeCourse, setIncludeCourse] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Filter
  const filtered = useMemo(() => {
    let result = questions || [];
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter((r) => r.content?.toLowerCase().includes(q));
    }
    if (typeFilter.length > 0) {
      result = result.filter((r) => typeFilter.includes(r.question_type));
    }
    if (sourceFilter.length > 0) {
      result = result.filter((r) => sourceFilter.includes(r.source));
    }
    return result;
  }, [questions, searchText, typeFilter, sourceFilter]);

  // Group by stimulus
  const grouped = useMemo(() => {
    const groups = {};
    const ungrouped = [];
    for (const q of filtered) {
      if (q.stimulus_id) {
        if (!groups[q.stimulus_id]) groups[q.stimulus_id] = [];
        groups[q.stimulus_id].push(q);
      } else {
        ungrouped.push(q);
      }
    }
    return { stimulusGroups: Object.entries(groups), ungrouped };
  }, [filtered]);

  const toggleTypeFilter = (type) => {
    setTypeFilter((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const toggleSourceFilter = (source) => {
    setSourceFilter((prev) =>
      prev.includes(source) ? prev.filter((s) => s !== source) : [...prev, source]
    );
  };

  const handleToggleCourse = () => {
    const next = !includeCourse;
    setIncludeCourse(next);
    router.reload({
      data: { include_course: next ? 'true' : 'false' },
      only: ['questions'],
    });
  };

  const handleCreated = () => {
    setShowCreate(false);
    router.reload();
  };

  const scrollTitle = (
    <>
      <Link
        href={`/teacher/courses/${lesson.course_id || ''}`}
        className="hover:text-blue-600 dark:hover:text-blue-400"
      >
        {t('questions.bank')}
      </Link>
      {'  ›  '}
      {lesson.title}
    </>
  );

  return (
    <TeacherShell scrollTitle={scrollTitle}>
      <TeacherPageHeader
        title={t('questions.bank')}
        subtitle={`${filtered.length} ${t('questions.questionsIn')} ${lesson.title}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowImport(true)}>
              {t('questions.import')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
              {t('questions.createQuestion')}
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Search */}
        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={t('questions.searchPlaceholder')}
          className={cn(
            'px-3 py-1.5 rounded-lg text-sm w-64',
            'bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-600',
            'text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500',
            'focus:outline-none focus:border-gray-400 dark:focus:border-gray-500'
          )}
        />

        {/* Type filters */}
        <div className="flex gap-1">
          {['mcq', 'short_answer', 'essay', 'fill_blank'].map((type) => (
            <button
              key={type}
              onClick={() => toggleTypeFilter(type)}
              className={cn(
                'px-2 py-1 rounded text-xs font-medium transition-all duration-200',
                typeFilter.includes(type)
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              )}
            >
              {TYPE_LABELS[type] || type}
            </button>
          ))}
        </div>

        {/* Source filters */}
        <div className="flex gap-1">
          {['ai_generated', 'manual', 'imported'].map((source) => (
            <button
              key={source}
              onClick={() => toggleSourceFilter(source)}
              className={cn(
                'px-2 py-1 rounded text-xs font-medium transition-all duration-200',
                sourceFilter.includes(source)
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              )}
            >
              {source === 'ai_generated' ? 'AI' : source.charAt(0).toUpperCase() + source.slice(1)}
            </button>
          ))}
        </div>

        {/* Course toggle */}
        {course_lessons?.length > 0 && (
          <button
            onClick={handleToggleCourse}
            className={cn(
              'px-2 py-1 rounded text-xs font-medium transition-all duration-200',
              includeCourse
                ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            )}
          >
            {t('questions.includeCourse')}
          </button>
        )}
      </div>

      {/* Create Form */}
      {showCreate && (
        <Card className="p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">{t('questions.createQuestion')}</h3>
          <QuestionCreationForm
            lessonId={lesson.id}
            stimuli={stimuli}
            onCreated={handleCreated}
            onCancel={() => setShowCreate(false)}
          />
        </Card>
      )}

      {/* Question List */}
      {filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {questions?.length === 0 ? t('questions.noQuestions') : t('questions.noQuestionsMatch')}
          </p>
          {questions?.length === 0 && (
            <div className="flex items-center justify-center gap-3">
              <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>{t('questions.createQuestion')}</Button>
              <Button variant="secondary" size="sm" onClick={() => setShowImport(true)}>{t('questions.importQuestions')}</Button>
            </div>
          )}
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Stimulus-grouped questions */}
          {grouped.stimulusGroups.map(([stimulusId, qs]) => (
            <div key={stimulusId}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-4 bg-gray-300 dark:bg-gray-600 rounded-full" />
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('questions.stimulus')}
                </p>
              </div>
              <div className="space-y-2">
                {qs.map((q) => (
                  <BankQuestionCard
                    key={q.id}
                    question={q}
                    editing={editingId === q.id}
                    expanded={expandedId === q.id}
                    onEdit={() => setEditingId(editingId === q.id ? null : q.id)}
                    onExpand={() => setExpandedId(expandedId === q.id ? null : q.id)}
                    onSaved={() => { setEditingId(null); router.reload(); }}
                    lessonId={lesson.id}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Ungrouped questions */}
          {grouped.ungrouped.length > 0 && (
            <div>
              {grouped.stimulusGroups.length > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1 h-4 bg-gray-200 dark:bg-gray-700 rounded-full" />
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('questions.noStimulus')}
                  </p>
                </div>
              )}
              <div className="space-y-2">
                {grouped.ungrouped.map((q) => (
                  <BankQuestionCard
                    key={q.id}
                    question={q}
                    editing={editingId === q.id}
                    expanded={expandedId === q.id}
                    onEdit={() => setEditingId(editingId === q.id ? null : q.id)}
                    onExpand={() => setExpandedId(expandedId === q.id ? null : q.id)}
                    onSaved={() => { setEditingId(null); router.reload(); }}
                    lessonId={lesson.id}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <ImportModal lessonId={lesson.id} onClose={() => setShowImport(false)} />
      )}
    </TeacherShell>
  );
}

// ── Bank Question Card ──────────────────────────────────────

function BankQuestionCard({ question, editing, expanded, onEdit, onExpand, onSaved, lessonId }) {
  const q = question;
  const isMcq = q.question_type === 'mcq' || q.question_type === 'multiple_choice';

  if (editing) {
    return (
      <Card className="p-4">
        <InlineQuestionEditor question={q} onSaved={onSaved} onCancel={onEdit} />
      </Card>
    );
  }

  return (
    <Card className="p-4 hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)] transition-shadow duration-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Badges */}
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
              {TYPE_LABELS[q.question_type] || q.question_type}
            </span>
            <SourceBadge source={q.source} />
            {q.bloom_level && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500 capitalize">
                {q.bloom_level}
              </span>
            )}
          </div>

          {/* Prompt */}
          <p
            className={cn(
              'text-sm text-gray-900 dark:text-white',
              !expanded && 'line-clamp-2'
            )}
            onClick={onExpand}
          >
            {q.content}
          </p>

          {/* MCQ options (when expanded) */}
          {expanded && isMcq && q.options && (
            <div className="mt-2 space-y-1">
              {Object.values(q.options).map((opt, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <span className={cn(
                    'w-5 h-5 rounded flex items-center justify-center text-[10px] font-medium',
                    opt.correct
                      ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                      : 'bg-gray-100 dark:bg-gray-700'
                  )}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span>{opt.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Revision info */}
          {expanded && q.source === 'ai_generated' && q.revised_at && (
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
              Originally AI-generated · Last edited {new Date(q.revised_at).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1.5 rounded text-gray-400 hover:text-gray-900 hover:bg-gray-100 dark:hover:text-white dark:hover:bg-gray-700 transition-colors"
            title="Edit"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Stimulus suggestions */}
      {q.metadata?.stimulus_suggestions && (
        <StimulusSuggestionBanner
          suggestions={q.metadata.stimulus_suggestions}
          onLink={(suggestion) => {
            // TODO: Link stimulus via PATCH
          }}
          onDismiss={() => {
            // TODO: Clear suggestion via PATCH
          }}
          className="mt-3"
        />
      )}
    </Card>
  );
}

// ── Inline Question Editor ──────────────────────────────────

function InlineQuestionEditor({ question, onSaved, onCancel }) {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState(question.content || '');
  const [questionType, setQuestionType] = useState(question.question_type);
  const [options, setOptions] = useState(() => {
    if (question.options) {
      return Object.values(question.options).map((opt) => ({
        text: opt.text || opt,
        correct: !!opt.correct,
      }));
    }
    return [
      { text: '', correct: true },
      { text: '', correct: false },
      { text: '', correct: false },
      { text: '', correct: false },
    ];
  });
  const [correctAnswer, setCorrectAnswer] = useState(question.correct_answer || '');
  const [submitting, setSubmitting] = useState(false);

  const isMcq = questionType === 'mcq' || questionType === 'multiple_choice';

  const handleSave = async () => {
    setSubmitting(true);
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    const body = {
      question: {
        prompt,
        question_type: questionType,
        correct_answer: isMcq ? options.find((o) => o.correct)?.text || '' : correctAnswer,
        options: isMcq ? options.reduce((acc, opt, i) => ({ ...acc, [i]: opt }), {}) : null,
      },
    };

    try {
      const res = await fetch(`/teacher/questions/${question.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken, Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) onSaved?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <QuestionTypeSelector value={questionType} onChange={setQuestionType} />

      <Textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        autoExpand
        placeholder="Question prompt"
      />

      {isMcq && <OptionsBuilder options={options} onChange={setOptions} />}

      {!isMcq && (
        <Textarea
          value={correctAnswer}
          onChange={(e) => setCorrectAnswer(e.target.value)}
          rows={2}
          placeholder="Correct answer"
        />
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={!prompt.trim() || submitting}>
          {submitting ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </div>
  );
}
